import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Typography, TextField, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, CheckCircle } from '@mui/icons-material';
import {
  fetchReversals, createReversal, approveReversal, clearError,
} from '../../../store/slices/ledgerSlice';

const STATUS_COLOR = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN') : '-';

const ReversalRequests = () => {
  const dispatch = useDispatch();
  const { reversals, loading, error } = useSelector(s => s.ledger);

  const [createOpen, setCreateOpen] = useState(false);
  const [entryUUID, setEntryUUID] = useState('');
  const [reason, setReason] = useState('');
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    dispatch(fetchReversals());
  }, [dispatch]);

  const handleCreate = async () => {
    if (!entryUUID.trim() || !reason.trim()) return;
    const result = await dispatch(createReversal({ original_entry_uuid: entryUUID.trim(), reason: reason.trim() }));
    if (!result.error) {
      setCreateOpen(false);
      setEntryUUID('');
      setReason('');
      dispatch(fetchReversals());
    }
  };

  const handleApprove = async (id) => {
    setApproving(id);
    const result = await dispatch(approveReversal(id));
    setApproving(null);
    if (!result.error) {
      dispatch(fetchReversals());
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Reversal requests require maker-checker dual approval. A different admin must approve.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="small"
          onClick={() => { setCreateOpen(true); dispatch(clearError()); }}
        >
          New Reversal
        </Button>
      </Box>

      {error.reversals && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error.reversals}
        </Alert>
      )}

      {loading.reversals ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Original Entry</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Maker</TableCell>
                <TableCell>Checker</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!reversals || reversals.length === 0) && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">No reversal requests</Typography>
                  </TableCell>
                </TableRow>
              )}
              {(reversals || []).map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {r.id?.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {r.original_entry_uuid?.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.reason}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} />
                  </TableCell>
                  <TableCell>{r.maker_id}</TableCell>
                  <TableCell>{r.checker_id ?? '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>{fmtDate(r.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.status === 'PENDING' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircle />}
                        disabled={approving === r.id}
                        onClick={() => handleApprove(r.id)}
                      >
                        {approving === r.id ? 'Approving…' : 'Approve'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Reversal Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Reversal Request</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error.createReversal && (
              <Alert severity="error">{error.createReversal}</Alert>
            )}
            <TextField
              label="Original Entry UUID"
              fullWidth
              size="small"
              value={entryUUID}
              onChange={(e) => setEntryUUID(e.target.value)}
              helperText="Copy from the ledger entries table"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            <TextField
              label="Reason"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Provide a detailed justification for this reversal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!entryUUID.trim() || !reason.trim() || loading.createReversal}
          >
            {loading.createReversal ? 'Creating…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReversalRequests;
